import React, { useState, useContext } from 'react';
import {
  View, Text, SectionList, Alert, RefreshControl, Pressable, Platform, UIManager, LayoutAnimation,
} from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card, Title, Paragraph, Button, Appbar, Chip, Avatar,
} from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';

import {
  GET_BAGS,
} from '../../gql/bag/query';
import {
  CREATE_BAG, DELETE_BAG, RENT_BAG, RETURN_BAG, UPDATE_BAG, CANCEL_RENTAL,
} from '../../gql/bag/mutation';
import { styles } from './BagsTab.style';
import { AllUsersQuery } from '../../gql/user/query';
import { GET_CURRENT_SEASON } from '../../gql/season/query';
import { SCREEN_NAMES } from '../../utils/constant';
import BagDialogs from './BagDialogs';
import LoadingIndicator from '../../components/commons/LoadingIndicator';
import { ModalContext } from '../../context/ModalContext';
import ConfirmationModal from '../../modals/Confirmation/ConfirmationModal';

if (
  Platform.OS === 'android'
  && UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const getDominantSize = (bag) => {
  if (!bag.equipments || bag.equipments.length === 0) {
    return 'No Size';
  }

  const sizeCounts = bag.equipments.reduce((acc, eq) => {
    const size = eq.equipment && eq.equipment.size;
    if (size) {
      acc[size] = (acc[size] || 0) + 1;
    }
    return acc;
  }, {});

  if (Object.keys(sizeCounts).length === 0) {
    return 'No Size';
  }

  const dominantSize = Object.keys(sizeCounts).reduce((a, b) => (sizeCounts[a] > sizeCounts[b] ? a : b));
  return dominantSize;
};

function EmptyBags({ onAdd }) {
  return (
    <View style={styles.emptyContainer}>
      <Avatar.Icon size={80} icon="bag-personal-outline" />
      <Text style={styles.emptyText}>Aucun sac n'a été trouvé.</Text>
      <Button mode="contained" onPress={onAdd} style={{ marginTop: 20 }}>
        Ajouter un sac
      </Button>
    </View>
  );
}

function BagsTab() {
  const navigation = useNavigation();
  const {
    data, loading, error, refetch,
  } = useQuery(GET_BAGS);

  const {
    data: usersData,
  } = useQuery(AllUsersQuery);

  const { data: dataSeason } = useQuery(GET_CURRENT_SEASON);
  const season = dataSeason?.findCurrentSeason;

  const {
    toggleConfirmationModal,
    displayConfirmationModal,
  } = useContext(ModalContext);

  const [createBag] = useMutation(CREATE_BAG);
  const [updateBag] = useMutation(UPDATE_BAG);
  const [deleteBag] = useMutation(DELETE_BAG);
  const [rentBag] = useMutation(RENT_BAG);
  const [returnBag] = useMutation(RETURN_BAG);
  const [cancelRental] = useMutation(CANCEL_RENTAL);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [currentBag, setCurrentBag] = useState(null);
  const [name, setName] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [rentDialogVisible, setRentDialogVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [bagToDeleteId, setBagToDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [returnDialogVisible, setReturnDialogVisible] = useState(false);
  const [rentalToReturnId, setRentalToReturnId] = useState(null);
  const [depositReturned, setDepositReturned] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [returnPicture, setReturnPicture] = useState(null);
  const [rentalComment, setRentalComment] = useState('');
  const [rentalPicture, setRentalPicture] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (title) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setCollapsedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const showDialog = (bag = null) => {
    setCurrentBag(bag);
    setName(bag ? bag.name : '');
    setDepositAmount(bag ? String(bag.depositAmount) : '');
    setDialogVisible(true);
  };

  const hideDialog = () => setDialogVisible(false);
  const showRentDialog = (bag) => {
    setCurrentBag(bag);
    setRentDialogVisible(true);
  };
  const hideRentDialog = () => setRentDialogVisible(false);
  const showDeleteDialog = (id) => {
    setBagToDeleteId(id);
    setDeleteDialogVisible(true);
  };
  const hideDeleteDialog = () => {
    setBagToDeleteId(null);
    setDeleteDialogVisible(false);
  };

  const showReturnDialog = (bag, rentalId) => {
    setCurrentBag(bag);
    setRentalToReturnId(rentalId);
    setReturnDialogVisible(true);
  };

  const hideReturnDialog = () => {
    setRentalToReturnId(null);
    setReturnDialogVisible(false);
    setDepositReturned(false);
    setReturnComment('');
    setReturnPicture(null);
  };

  const hideRentDialogFull = () => {
    setRentalComment('');
    setRentalPicture(null);
    hideRentDialog();
  };

  const handleSave = async () => {
    try {
      if (currentBag) {
        await updateBag({
          variables: {
            id: currentBag.id,
            name,
            depositAmount: parseFloat(depositAmount),
          },
        });
      } else {
        await createBag({
          variables: {
            name,
            depositAmount: parseFloat(depositAmount),
          },
        });
      }
      refetch();
      hideDialog();
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  };

  const handleDelete = (id) => {
    showDeleteDialog(id);
  };

  const confirmDelete = async () => {
    try {
      await deleteBag({ variables: { id: bagToDeleteId } });
      refetch();
      hideDeleteDialog();
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  };

  const handleRent = async () => {
    try {
      if (currentBag && selectedUser) {
        // Extract base64 from picture if present
        let pictureBase64 = null;
        if (rentalPicture && rentalPicture.uri) {
          if (rentalPicture.uri.startsWith('data:image')) {
            const [, base64Data] = rentalPicture.uri.split(',');
            pictureBase64 = base64Data;
          } else {
            pictureBase64 = rentalPicture.uri;
          }
        }

        await rentBag({
          variables: {
            bagId: currentBag.id,
            userId: selectedUser.id,
            depositPaid: true,
            rentalComment: rentalComment || null,
            rentalPicture: pictureBase64,
          },
        });
        refetch();
        hideRentDialogFull();
      }
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  };

  const handleReturn = async () => {
    try {
      // Extract base64 from picture if present
      let pictureBase64 = null;
      if (returnPicture && returnPicture.uri) {
        if (returnPicture.uri.startsWith('data:image')) {
          const [, base64Data] = returnPicture.uri.split(',');
          pictureBase64 = base64Data;
        } else {
          pictureBase64 = returnPicture.uri;
        }
      }

      await returnBag({
        variables: {
          rentalId: rentalToReturnId,
          depositReturned,
          returnComment: returnComment || null,
          returnPicture: pictureBase64,
        },
      });
      refetch();
      hideReturnDialog();
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  };

  const handleCancelRental = (rentalId) => {
    toggleConfirmationModal({
      title: 'Annuler la location',
      message: 'Etes-vous sûr de vouloir annuler cette location ?',
      handleSubmit: async () => {
        try {
          await cancelRental({ variables: { rentalId } });
          refetch();
        } catch (e) {
          Alert.alert('Error', e.message);
        }
      },
    });
  };

  const onRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  if (loading) {
    return <LoadingIndicator text="Chargement des sacs..." />;
  }
  if (error) {
    return (
      <Text>
        Error:
        {error.message}
      </Text>
    );
  }

  const bagsBySize = data.bags.reduce((acc, bag) => {
    const size = getDominantSize(bag);
    if (!acc[size]) {
      acc[size] = [];
    }
    acc[size].push(bag);
    return acc;
  }, {});

  const stats = Object.entries(bagsBySize).map(([size, bags]) => {
    const rentedCount = bags.filter((bag) => bag.rentals.some((r) => !r.returnDate)).length;
    const freeCount = bags.length - rentedCount;
    return { size, rentedCount, freeCount };
  });

  const sections = Object.entries(bagsBySize)
    .map(([size, bags]) => ({
      title: `Taille ${size}`,
      data: [...bags].sort((a, b) => {
        const aIsRented = a.rentals.some((r) => !r.returnDate);
        const bIsRented = b.rentals.some((r) => !r.returnDate);
        return aIsRented - bIsRented;
      }),
    }))
    .sort((a, b) => a.title.localeCompare(b.title));

  const renderBagItem = ({ item }) => {
    const currentRental = item.rentals.find((rental) => !rental.returnDate);
    const isRented = !!currentRental;
    const cardStyle = isRented ? styles.rentedCard : styles.freeCard;
    const dominantSize = getDominantSize(item);

    return (
      <Card style={[styles.card, cardStyle]}>
        <Card.Content style={styles.cardContent}>
          <View style={styles.cardHeader}>
            <Title style={styles.cardTitle}>{item.name || `Sac ${item.id}`}</Title>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              {dominantSize !== 'No Size' && (
              <Chip style={styles.statusChip} icon="hanger">
                {dominantSize}
              </Chip>
              )}
              <Chip style={styles.statusChip} icon={isRented ? 'lock' : 'lock-open-variant'}>
                {isRented ? 'Loué' : 'Libre'}
              </Chip>
            </View>
          </View>
          <Paragraph>
            Caution:
            {item.depositAmount}
            {' '}
            €
          </Paragraph>
          {isRented && (
            <View style={styles.rentedInfo}>
              <Avatar.Image size={30} source={{ uri: currentRental.user.profile?.pictureThumbnail }} style={{ marginRight: 8 }} />
              <Text>
                {currentRental.user.firstName}
                {' '}
                {currentRental.user.lastName}
              </Text>
            </View>
          )}
          <Text style={styles.infoText}>
            Equipements:
            {item.equipments.length}
          </Text>
        </Card.Content>
        <Card.Actions>
          {!isRented ? (
            <>
              <Button onPress={() => showDialog(item)}>Modifier</Button>
              <Button onPress={() => handleDelete(item.id)}>Supprimer</Button>
              <Button mode="contained" onPress={() => showRentDialog(item)}>Louer à</Button>
            </>
          ) : (
            <>
              <Button onPress={() => handleCancelRental(currentRental.id)} textColor="red">Annuler</Button>
              <Button mode="contained" onPress={() => showReturnDialog(item, currentRental.id)}>Effectuer un retour</Button>
            </>
          )}
          <Button onPress={() => navigation.navigate(SCREEN_NAMES.BAG_DETAILS, { qrCode: item.qrCode })}>Détails</Button>
        </Card.Actions>
      </Card>
    );
  };

  const sectionsWithCollapse = sections.map((section) => ({
    ...section,
    data: collapsedSections[section.title] ? [] : section.data,
  }));

  return (
    <>
      {displayConfirmationModal.display && <ConfirmationModal />}
      <Appbar.Header statusBarHeight={0}>
        <Appbar.Content title="Gestion des Sacs" />
        <Appbar.Action icon="plus-box" onPress={() => showDialog()} />
      </Appbar.Header>
      {stats.length > 0 && (
        <Card style={styles.statsCard}>
          <Card.Content>
            <View style={styles.statsHeader}>
              <Text style={styles.statsHeaderText}>Taille</Text>
              <Text style={styles.statsHeaderText}>Loués</Text>
              <Text style={styles.statsHeaderText}>Libres</Text>
            </View>
            {stats.map(({ size, rentedCount, freeCount }) => (
              <View key={size} style={styles.statsRow}>
                <Text style={[styles.statsSizeText, { flex: 1, textAlign: 'center' }]}>{size}</Text>
                <Text style={{ flex: 1, textAlign: 'center' }}>{rentedCount}</Text>
                <Text style={{ flex: 1, textAlign: 'center' }}>{freeCount}</Text>
              </View>
            ))}
          </Card.Content>
        </Card>
      )}
      <View style={styles.container}>
        <SectionList
          sections={sectionsWithCollapse}
          keyExtractor={(item) => item.id.toString()}
          renderItem={renderBagItem}
          renderSectionHeader={({ section: { title } }) => (
            <Pressable onPress={() => toggleSection(title)}>
              <View style={[styles.sectionHeader, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
                <Text style={styles.sectionHeaderText}>{title}</Text>
                <Avatar.Icon size={24} icon={collapsedSections[title] ? 'chevron-down' : 'chevron-up'} style={{ backgroundColor: 'transparent' }} color="black" />
              </View>
            </Pressable>
          )}
          ListEmptyComponent={<EmptyBags onAdd={() => showDialog()} />}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />}
        />
        <BagDialogs
          dialogVisible={dialogVisible}
          hideDialog={hideDialog}
          currentBag={currentBag}
          name={name}
          setName={setName}
          depositAmount={depositAmount}
          setDepositAmount={setDepositAmount}
          handleSave={handleSave}
          rentDialogVisible={rentDialogVisible}
          hideRentDialog={hideRentDialogFull}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          usersData={usersData}
          season={season}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          handleRent={handleRent}
          deleteDialogVisible={deleteDialogVisible}
          hideDeleteDialog={hideDeleteDialog}
          confirmDelete={confirmDelete}
          returnDialogVisible={returnDialogVisible}
          hideReturnDialog={hideReturnDialog}
          depositReturned={depositReturned}
          setDepositReturned={setDepositReturned}
          returnComment={returnComment}
          setReturnComment={setReturnComment}
          returnPicture={returnPicture}
          setReturnPicture={setReturnPicture}
          handleReturn={handleReturn}
          rentalComment={rentalComment}
          setRentalComment={setRentalComment}
          rentalPicture={rentalPicture}
          setRentalPicture={setRentalPicture}
        />
      </View>
    </>
  );
}

export default BagsTab;
